<template>
    <div class="max-w-4xl mx-auto">
        <div class="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
            <!-- Page Header -->
            <div
                class="px-6 py-4 border-b border-gray-200 dark:border-gray-700"
            >
                <h1
                    class="text-2xl font-semibold text-gray-900 dark:text-white"
                >
                    Profile Settings
                </h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Manage your account information and password
                </p>
            </div>

            <!-- Profile Form -->
            <form @submit.prevent="handleSubmit" class="p-6 space-y-6">
                <!-- User Information Section -->
                <div>
                    <h3
                        class="text-lg font-medium text-gray-900 dark:text-white mb-4"
                    >
                        User Information
                    </h3>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Name Field -->
                        <div>
                            <label
                                for="name"
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Full Name
                            </label>
                            <input
                                id="name"
                                v-model="form.name"
                                type="text"
                                required
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                :disabled="isLoading || !isAdmin"
                                :readonly="!isAdmin"
                            />
                            <p
                                v-if="!isAdmin"
                                class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                            >
                                Only administrators can change the name
                            </p>
                        </div>

                        <!-- Email Field (Read-only) -->
                        <div>
                            <label
                                for="email"
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Email Address
                            </label>
                            <input
                                id="email"
                                :value="profile?.email"
                                type="email"
                                readonly
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                            />
                            <p
                                class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                            >
                                Email cannot be changed here
                            </p>
                        </div>

                        <!-- Role Field (Read-only) -->
                        <div>
                            <label
                                for="role"
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Role
                            </label>
                            <input
                                id="role"
                                :value="userRole"
                                type="text"
                                readonly
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                            />
                        </div>
                    </div>

                    <!-- Role-specific Information -->
                    <div
                        v-if="isStudent"
                        class="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                    >
                        <h4
                            class="text-lg font-medium text-blue-900 dark:text-blue-100 mb-4"
                        >
                            Student Information
                        </h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Student ID
                                </label>
                                <input
                                    :value="studentProfile?.student_id"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    NISN
                                </label>
                                <input
                                    :value="studentProfile?.nisn"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Birth Date
                                </label>
                                <input
                                    :value="studentProfile?.birth_date"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Gender
                                </label>
                                <input
                                    :value="studentProfile?.gender"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Phone
                                </label>
                                <input
                                    :value="studentProfile?.phone"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Address
                                </label>
                                <input
                                    :value="studentProfile?.address"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Emergency Contact
                                </label>
                                <input
                                    :value="studentProfile?.emergency_contact"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Emergency Phone
                                </label>
                                <input
                                    :value="studentProfile?.emergency_phone"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Current Class
                                </label>
                                <input
                                    :value="studentProfile?.currentClass?.name"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Enrollment Date
                                </label>
                                <input
                                    :value="studentProfile?.enrollment_date"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Status
                                </label>
                                <input
                                    :value="
                                        studentProfile?.is_active
                                            ? 'Active'
                                            : 'Inactive'
                                    "
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="isTeacher"
                        class="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg"
                    >
                        <h4
                            class="text-lg font-medium text-green-900 dark:text-green-100 mb-4"
                        >
                            Teacher Information
                        </h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Teacher ID
                                </label>
                                <input
                                    :value="teacherProfile?.teacher_id"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    NIP
                                </label>
                                <input
                                    :value="teacherProfile?.nip"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Birth Date
                                </label>
                                <input
                                    :value="teacherProfile?.birth_date"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Gender
                                </label>
                                <input
                                    :value="teacherProfile?.gender"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Phone
                                </label>
                                <input
                                    :value="teacherProfile?.phone"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Address
                                </label>
                                <input
                                    :value="teacherProfile?.address"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Qualification
                                </label>
                                <input
                                    :value="teacherProfile?.qualification"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Specialization
                                </label>
                                <input
                                    :value="teacherProfile?.specialization"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Hire Date
                                </label>
                                <input
                                    :value="teacherProfile?.hire_date"
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Homeroom Status
                                </label>
                                <input
                                    :value="
                                        teacherProfile?.is_homeroom
                                            ? 'Yes'
                                            : 'No'
                                    "
                                    type="text"
                                    readonly
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Homeroom Classes
                                </label>
                                <div
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                                >
                                    <div
                                        v-if="
                                            teacherProfile?.homeroomClasses &&
                                            teacherProfile.homeroomClasses
                                                .length > 0
                                        "
                                    >
                                        <template
                                            v-for="homeroomClass in teacherProfile.homeroomClasses"
                                            :key="homeroomClass.id"
                                        >
                                            {{ homeroomClass.name
                                            }}<span
                                                v-if="
                                                    teacherProfile.homeroomClasses.indexOf(
                                                        homeroomClass,
                                                    ) <
                                                    teacherProfile
                                                        .homeroomClasses
                                                        .length -
                                                        1
                                                "
                                                >,
                                            </span>
                                        </template>
                                    </div>
                                    <div v-else>
                                        No homeroom classes assigned
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label
                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Subjects Taught
                                </label>
                                <div
                                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
                                >
                                    <div
                                        v-if="
                                            teacherProfile?.classSubjects &&
                                            teacherProfile.classSubjects
                                                .length > 0
                                        "
                                    >
                                        <template
                                            v-for="subject in teacherProfile.classSubjects"
                                            :key="subject.id"
                                        >
                                            {{
                                                subject.subject?.name ||
                                                subject.name
                                            }}<span
                                                v-if="
                                                    teacherProfile.classSubjects.indexOf(
                                                        subject,
                                                    ) <
                                                    teacherProfile.classSubjects
                                                        .length -
                                                        1
                                                "
                                                >,
                                            </span>
                                        </template>
                                    </div>
                                    <div v-else>No subjects assigned</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Password Section -->
                <div>
                    <h3
                        class="text-lg font-medium text-gray-900 dark:text-white mb-4"
                    >
                        Change Password
                    </h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Leave blank if you don't want to change your password
                    </p>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- New Password Field -->
                        <div>
                            <label
                                for="password"
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                New Password
                            </label>
                            <input
                                id="password"
                                v-model="form.password"
                                type="password"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                :disabled="isLoading"
                                placeholder="Enter new password"
                            />
                        </div>

                        <!-- Password Confirmation Field -->
                        <div>
                            <label
                                for="password_confirmation"
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                                Confirm New Password
                            </label>
                            <input
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                type="password"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                                :disabled="isLoading"
                                placeholder="Confirm new password"
                            />
                        </div>
                    </div>
                </div>

                <!-- Error Message -->
                <div
                    v-if="error"
                    class="rounded-md bg-red-50 dark:bg-red-900/20 p-4"
                >
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <ExclamationTriangleIcon
                                class="h-5 w-5 text-red-400"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="ml-3">
                            <h3
                                class="text-sm font-medium text-red-800 dark:text-red-200"
                            >
                                Error
                            </h3>
                            <div
                                class="mt-2 text-sm text-red-700 dark:text-red-300"
                            >
                                {{ error }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Success Message -->
                <div
                    v-if="successMessage"
                    class="rounded-md bg-green-50 dark:bg-green-900/20 p-4"
                >
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <CheckCircleIcon
                                class="h-5 w-5 text-green-400"
                                aria-hidden="true"
                            />
                        </div>
                        <div class="ml-3">
                            <h3
                                class="text-sm font-medium text-green-800 dark:text-green-200"
                            >
                                Success
                            </h3>
                            <div
                                class="mt-2 text-sm text-green-700 dark:text-green-300"
                            >
                                {{ successMessage }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div
                    class="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700"
                >
                    <button
                        type="button"
                        @click="resetForm"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        :disabled="isLoading"
                    >
                        Reset
                    </button>
                    <button
                        type="submit"
                        class="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="isLoading || !isFormValid"
                    >
                        <span v-if="isLoading" class="flex items-center">
                            <svg
                                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                ></circle>
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            Saving...
                        </span>
                        <span v-else>Save Changes</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useUserStore } from "@/stores/userStore";
import { useAuthStore } from "@/stores/authStore";
import {
    ExclamationTriangleIcon,
    CheckCircleIcon,
} from "@heroicons/vue/24/outline";

// Stores
const userStore = useUserStore();
const authStore = useAuthStore();

// Computed to check if user is admin
const isAdmin = computed(
    () =>
        authStore.hasRole("super-administrator") ||
        authStore.hasRole("administrator"),
);

// State
const isLoading = ref(false);
const error = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Form data
const form = ref({
    name: "",
    password: "",
    password_confirmation: "",
});

// Computed
const profile = computed(() => userStore.profile || authStore.user);
const userRole = computed(() => {
    const roles = profile.value?.roles || [];
    return roles.length > 0 ? roles[0].name : "User";
});

// Role-specific profile data
const isStudent = computed(() => userRole.value === "student");
const isTeacher = computed(() => userRole.value === "teacher");

const studentProfile = computed(() => {
    console.log("DEBUG: Getting student profile:", userStore.roleProfile);
    console.log("DEBUG: Is student role:", isStudent.value);
    return userStore.roleProfile;
});

const teacherProfile = computed(() => {
    return userStore.roleProfile;
});

const isFormValid = computed(() => {
    // Name is required
    if (!form.value.name.trim()) return false;

    // If password is provided, confirmation must match
    if (
        form.value.password &&
        form.value.password !== form.value.password_confirmation
    ) {
        return false;
    }

    // If password is provided, it should be at least 8 characters
    if (form.value.password && form.value.password.length < 8) {
        return false;
    }

    return true;
});

// Methods
const loadProfile = async () => {
    try {
        await userStore.fetchProfile();
        if (userStore.profile) {
            form.value.name = userStore.profile.name || "";
        }
    } catch (err) {
        error.value = "Failed to load profile data";
    }
};

const handleSubmit = async () => {
    if (!isFormValid.value) return;

    try {
        isLoading.value = true;
        error.value = null;
        successMessage.value = null;

        const submitData: any = {
            name: form.value.name,
        };

        // Only include password fields if password is provided
        if (form.value.password) {
            submitData.password = form.value.password;
            submitData.password_confirmation = form.value.password_confirmation;
        }

        await userStore.updateUserProfile(submitData);

        // Update auth store user data as well
        if (authStore.user && userStore.profile) {
            // Convert the profile to match the User interface
            const userForAuth = {
                ...userStore.profile,
                is_active: userStore.profile.status || true, // Use status or default to true
                roles:
                    userStore.profile.roles?.map((role: any) => ({
                        ...role,
                        created_at: role.created_at || new Date().toISOString(),
                        updated_at: role.updated_at || new Date().toISOString(),
                    })) || [],
            };
            authStore.setUser(userForAuth);
        }

        successMessage.value = "Profile updated successfully!";

        // Clear password fields after successful update
        form.value.password = "";
        form.value.password_confirmation = "";
    } catch (err: any) {
        error.value =
            err.response?.data?.message ||
            err.message ||
            "Failed to update profile";
    } finally {
        isLoading.value = false;
    }
};

const resetForm = () => {
    if (profile.value) {
        form.value.name = profile.value.name || "";
    }
    form.value.password = "";
    form.value.password_confirmation = "";
    error.value = null;
    successMessage.value = null;
};

// Lifecycle
onMounted(() => {
    loadProfile();
});
</script>
